import type { AgentFocus } from '@tunasong/agent-context'
import { useAudioEngine } from '@tunasong/audio-ui'
import { shortUuid } from '@tunasong/models'
import { AssistantAvatarContext, Chat } from '@tunasong/plugin-chat/ui'
import type { ChatProps } from '@tunasong/plugin-chat/ui'
import { useEditor, usePlugins } from '@tunasong/plugin-lib'
import { features, nlpApi, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { Agent, ColorName, Comment, Persisted } from '@tunasong/schemas'
import { Avatar, getColorCSS } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { Node } from 'slate'
import styles from './agent-chat.module.css'
import { resolveAgentVisuals } from './agent-visuals.js'
import { LiveAgentResponse } from './live-agent-response.js'
import { useClientToolHost } from './use-client-tool-host.js'
import { useInFlightAgentStream } from './use-in-flight-agent-stream.js'

/**
 * Same shape as ChatProps but with `element` narrowed to Agent — agentStream
 * only makes sense for agent entities, so we lock the type at the seam rather
 * than runtime-guard inside the handler.
 *
 * `pendingDraft` / `onDraftConsumed` / `chatBoxId` are passthroughs to the
 * underlying ChatBox so callers (the sidebar empty-state header) can pre-fill
 * the input and wire aria-describedby without reaching into Chat's internals.
 */
export interface AgentChatProps extends Omit<ChatProps, 'element'> {
  element: Persisted<Agent> | Agent
}

/**
 * Read the text body of a chat comment by walking its Slate tree.
 * Returns '' for empty or whitespace-only comments so callers can short-circuit.
 */
const commentText = (comment: Comment): string =>
  (comment.children ?? [])
    .map(child => Node.string(child as never))
    .join('\n')
    .trim()

/**
 * Wraps Chat with agent-specific behavior: when the LOCAL user posts a comment
 * via the ChatBox, fire nlpApi.agentStream so runAgent runs server-side. The
 * agent's response is persisted as another comment child by runAgent and
 * arrives at this thread via the normal entity-push pipeline — so we do NOT
 * need to render the streamed text here. (Streaming-bubble UI is a follow-up.)
 *
 * Skipping conditions:
 * - `userId === 'assistant'` — the comment is the agent's own response; firing
 *   would create an invocation loop.
 * - empty text — submit-on-enter and similar UX bugs can produce empty
 *   comments; we don't spend an LLM round-trip on them.
 */
export const AgentChat: FC<AgentChatProps> = props => {
  const { element: agent } = props
  const dispatch = useThunkDispatch()

  // Audio engine context — may not be present (audio plugin not mounted on
  // this view). Pass allowNull=true so we get null instead of a throw.
  const audioEngine = useAudioEngine(true)

  // Editor + plugins for client-tool dispatch. AgentChat renders inside the
  // sidebar, which `EntityChrome` mounts *within* its `TunaEditorContext`
  // provider (entity-chrome.tsx wraps <Sidebar> in the provider and feeds it
  // the live editor via onEditor). So the editor in view IS reachable here.
  // allowNull: the agent can also surface outside an editor (returns null →
  // apply_editor_command degrades to `no_editor` gracefully). The context
  // value is the stable editor *instance* reference — Slate mutates
  // editor.selection in place, so reading it here does not re-render on every
  // caret move; only a document switch changes the reference.
  // PluginContext is app-root (di-container), above the sidebar, so usePlugins
  // resolves anywhere in the tree.
  const editor = useEditor({ allowNull: true })
  const plugins = usePlugins('all') ?? []

  // Register a client-tool dispatcher for this agent. editor + plugins are
  // captured fresh each render and read at dispatch time via useClientToolHost's
  // getContextRef, so a document switch propagates to the next tool call.
  // Invariant: the agent is attached to the viewed entity, so `editor` roots
  // the agent's parent (mirrors the editor.rootElement.id === targetEntityId
  // check in proposal-apply.ts). apply_editor_command stays allowlisted to
  // non-destructive UI commands regardless.
  // `agent.id` is `string | undefined` on the bare Agent union; AgentChat only
  // ever renders for a persisted agent, so it is present at runtime. Coalesce
  // to '' to satisfy the `string` param — an empty agentId registers a
  // dispatcher that the agentStream handler never looks up (real streams carry
  // a real id), so it is inert.
  const { resetCounter } = useClientToolHost(agent.id ?? '', () => ({
    editor,
    plugins,
    audioEngine,
  }))

  // Subscribe to the in-flight `agentStream` cache entry for this agent so we
  // can render the live response below the persisted-comment feed. The hook
  // returns `null` when no stream is in flight (or when the cache entry has
  // evicted post-completion via `keepUnusedDataFor: 0`).
  const liveStream = useInFlightAgentStream(agent.id)

  // One-shot anchor handed off by a block-affordance click. Only honor it
  // when the sidebar's active agent matches THIS chat tab — otherwise the
  // chip would bleed into a sibling agent's composer.
  const pendingFocusRaw = useSelector(state => state.ui.sidebar.pendingFocus)
  const activeAgentId = useSelector(state => state.ui.sidebar.activeAgentId)
  const pendingFocus: AgentFocus | null = activeAgentId === agent.id ? pendingFocusRaw : null

  // Per-thread avatar override: `userId === 'assistant'` comments in this
  // chat are from this agent, so render the agent's resolved icon + color.
  // The factory is stable across renders for the same agent identity — React
  // Compiler memoises the body, so no manual useCallback/useMemo needed.
  const { color, Icon } = resolveAgentVisuals(agent)
  const backgroundColor = getColorCSS(color as ColorName)
  const AgentAvatar: FC = () => (
    <Avatar style={{ backgroundColor, color: 'var(--tuna-content-on-color, #fff)' }}>
      <Icon aria-hidden="true" />
    </Avatar>
  )

  const handleComment = (comment: Comment) => {
    if (comment.userId === 'assistant') {
      return
    }
    const text = commentText(comment)
    if (text.length === 0) {
      return
    }
    const agentId = agent.id
    if (!agentId) {
      return
    }
    // New turn — reset the per-turn rate-limit counter on the dispatcher.
    resetCounter()
    // `requestId` is required by `AgentStreamPayload` so RTK Query keys
    // don't collapse across repeated chat submissions (an identical
    // (agentId, message, trigger) tuple would otherwise hit the cached
    // stream instead of running the agent again). Fresh nonce per submit.
    void dispatch(
      nlpApi.endpoints.agentStream.initiate({
        agentId,
        message: text,
        trigger: { type: 'chat' },
        // requestId is required on AgentStreamPayload — unique per submit so
        // RTK Query cache keys don't collapse across re-asks.
        requestId: shortUuid(),
        // Stable per-turn id for the Phase 3 continuation handshake. Lambda
        // uses this as the turn-state DDB key when the LLM emits a
        // client-side tool call.
        turnId: shortUuid(),
        // Spread `focus` only when present so the payload shape stays clean
        // for plain chat sends (no anchor).
        ...(pendingFocus ? { focus: pendingFocus } : {}),
      })
    )
    // One-shot semantics: consume the pending anchor so the next submit is
    // a plain chat send unless the user clicks another block affordance.
    if (pendingFocus) {
      dispatch(features.ui.actions.setSidebarPendingFocus(null))
    }
  }

  // Cast: ChatProps.element is Entity (wider). Agent is in the Entity union so
  // the runtime value is fine; the cast tells TS to forward the narrower type
  // through Chat's wider slot.
  const focusLabel = pendingFocus ? (pendingFocus.summary ?? pendingFocus.id) : ''
  return (
    <AssistantAvatarContext.Provider value={AgentAvatar}>
      {pendingFocus ? (
        <div
          className={styles.focusChip}
          aria-label={`Focus: ${pendingFocus.type} · ${focusLabel}`}
          data-testid="focus-chip"
        >
          <span>On {focusLabel}</span>
          <button
            type="button"
            aria-label="dismiss focus"
            onClick={() => dispatch(features.ui.actions.setSidebarPendingFocus(null))}
          >
            ×
          </button>
        </div>
      ) : null}
      <Chat {...(props as ChatProps)} onComment={handleComment} />
      {liveStream ? <LiveAgentResponse stream={liveStream} accentColor={backgroundColor} /> : null}
    </AssistantAvatarContext.Provider>
  )
}
